import React, { useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Paper,
  Box,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PictureAsPdf,
  Share,
  BookmarkBorder,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Article, PdfFile } from '../services/articleService';
import { articleService } from '../services/articleService';

const ArticleDetail: React.FC = () => {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const [currentArticle, setCurrentArticle] = React.useState<Article | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const article = await articleService.getArticleById(id);
        
        // Проверяем, соответствует ли slug в URL текущей статье
        const currentSlug = articleService.createSlug(article.title);
        if (slug !== currentSlug) {
          // Если slug не соответствует, перенаправляем на правильный URL
          window.history.replaceState(
            null,
            '',
            `/articles/${article.id}/${currentSlug}`
          );
        }
        
        setCurrentArticle(article);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке статьи');
        setCurrentArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id, slug]);

  const handleDownloadPdf = async () => {
    if (currentArticle?.pdfFile?.path) {
      try {
        // Увеличиваем счетчик скачиваний
        await articleService.incrementDownloads(currentArticle.id);
        
        // URL для PDF: /api/uploads/ отдаётся сервером
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const fileName = currentArticle.pdfFile.file;
        const pdfUrl = `${apiBase.replace(/\/api\/?$/, '')}/api/uploads/${fileName}`;
        console.log('Открываем PDF по пути:', pdfUrl);
        
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Ошибка при открытии PDF:', error);
      }
    } else {
      console.error('PDF файл не найден для статьи:', currentArticle?.id);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !currentArticle) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Статья не найдена'}</Alert>
        <Button
          component={RouterLink}
          to="/articles"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Вернуться к списку статей
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Helmet>
        <title>{currentArticle.title} | Тюменский научный журнал</title>
        <meta name="description" content={currentArticle.abstract} />
        <meta name="keywords" content={currentArticle.keywords.join(', ')} />
        <meta property="og:title" content={currentArticle.title} />
        <meta property="og:description" content={currentArticle.abstract} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://tyumen-science.ru/articles/${currentArticle.id}`} />
        <meta property="article:published_time" content={currentArticle.createdAt} />
        <meta property="article:author" content={currentArticle.authors?.map(a => `${a.firstName}${a.middleName ? ` ${a.middleName}` : ''} ${a.lastName}`).join(', ')} />
        <meta property="article:section" content={currentArticle.issue?.title} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentArticle.title} />
        <meta name="twitter:description" content={currentArticle.abstract} />
      </Helmet>
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/articles"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Вернуться к списку статей
        </Button>
      </Box>
      <Paper elevation={2} sx={{ p: 4 }}>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ScholarlyArticle",
            "headline": currentArticle.title,
            "author": currentArticle.authors?.map(author => ({
              "@type": "Person",
              "name": `${author.firstName}${author.middleName ? ` ${author.middleName}` : ''} ${author.lastName}`,
              "email": author.email
            })),
            "datePublished": currentArticle.createdAt,
            "publisher": {
              "@type": "Organization",
              "name": "Тюменский научный журнал",
              "url": "https://tyumen-science.ru"
            },
            "description": currentArticle.abstract,
            "keywords": currentArticle.keywords.join(", "),
            "doi": currentArticle.doi,
            "edn": currentArticle.edn,
            "url": `https://tyumen-science.ru/articles/${currentArticle.id}`,
            "isPartOf": currentArticle.issue ? {
              "@type": "PublicationIssue",
              "issueNumber": currentArticle.issue.number,
              "datePublished": `${currentArticle.issue.year}-01-01`
            } : undefined
          })}
        </script>
        <Typography variant="h4" component="h1" gutterBottom>
          {currentArticle.title}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Авторы:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {currentArticle.authors?.map((author, index) => (
              <Chip
                key={index}
                label={`${author.firstName}${author.middleName ? ` ${author.middleName}` : ''} ${author.lastName}`}
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Аннотация
          </Typography>
          <Typography variant="body1" paragraph>
            {currentArticle.abstract}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Ключевые слова
          </Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              height: '64px', // Увеличиваем высоту для полного отображения двух строк
              overflow: 'hidden'
            }}
          >
            {currentArticle.keywords.map((keyword, index) => (
              <Chip
                key={index}
                label={keyword}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ 
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Информация о публикации
          </Typography>
          {currentArticle.doi && (
            <Typography variant="body2" paragraph>
              <strong>DOI:</strong> {currentArticle.doi}
            </Typography>
          )}
          {currentArticle.edn && (
            <Typography variant="body2" paragraph>
              <strong>EDN:</strong> {currentArticle.edn}
            </Typography>
          )}
          <Typography variant="body2" paragraph>
            <strong>Журнал:</strong> Тюменский научный журнал
          </Typography>
          {currentArticle.issue && (
            <Typography variant="body2" paragraph>
              <strong>Выпуск:</strong> {currentArticle.issue.title}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          {currentArticle.pdfFile?.path && (
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              color="primary"
              onClick={handleDownloadPdf}
            >
              Скачать PDF
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={() => {
              navigator.share({
                title: currentArticle.title,
                text: currentArticle.abstract,
                url: window.location.href,
              }).catch(() => {
                // Fallback для браузеров, не поддерживающих Web Share API
                alert('Скопируйте ссылку из адресной строки');
              });
            }}
          >
            Поделиться
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ArticleDetail; 